/* FM VAT Toggle (08-Jun-2026)
 * =============================
 * Manages sitewide Ex. VAT / Inc. VAT price display.
 *
 * State is stored in localStorage key `fm_vat_mode`:
 *   'ex'  — Ex. VAT (default, B2B-first)
 *   'inc' — Inc. VAT (Google Ads visitors, or manual toggle)
 *
 * On page load:
 *   1. Check URL for Google Ads signals (gclid param, or
 *      utm_source=google + utm_medium=cpc). If found → set 'inc'.
 *   2. Read localStorage; default to 'ex' if unset.
 *   3. Apply mode: update all .fm-price elements + labels.
 *   4. Sync the toggle UI state.
 *
 * Price elements must carry data attributes (set in Liquid):
 *   data-ex-price   — Ex. VAT price string  (e.g. "£32.65")
 *   data-inc-price  — Inc. VAT price string (e.g. "£39.18")
 *
 * VAT label elements use class `fm-vat-label` with:
 *   data-ex-label   — e.g. "+ VAT" or "Ex. VAT"
 *   data-inc-label  — e.g. "Inc. VAT"
 *
 * Cart total uses class `fm-cart-vat-note` with:
 *   data-ex-note    — e.g. "Ex. VAT — VAT added at checkout"
 *   data-inc-note   — e.g. "Inc. VAT"
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'fm_vat_mode';
  var MODE_EX  = 'ex';
  var MODE_INC = 'inc';

  /* Re-entrancy guard. applyMode() writes textContent on elements that
     can sit inside an observed cart container — those writes fire the
     MutationObserver callback (a microtask). Without this guard the
     callback would call applyMode() again → its writes fire the
     callback again → infinite loop → frozen page. We set the flag for
     the duration of applyMode and release it on a macrotask (setTimeout
     0), which runs AFTER the microtask observer callbacks — so any
     callback triggered by our own writes sees the flag set and bails. */
  var fmApplying = false;

  /* ——— Detect a VAT-inclusive landing ———
     Returns true when the current page was reached via a channel that
     must show Inc VAT prices. Three signals:
       1. `incl_vat=true` — the param appended to Google Merchant Centre
          / Shopping feed URLs (the original mechanism; still in the
          live product feed). THIS is what Merchant Centre links carry.
       2. `gclid` — present on Google Ads clicks.
       3. `utm_source=google` + `utm_medium=cpc` — paid Google traffic.
     Any of these forces Inc for the session. */
  function isVatInclusiveLanding() {
    var params = new URLSearchParams(window.location.search);
    if ((params.get('incl_vat') || '').toLowerCase() === 'true') return true;
    if (params.get('gclid')) return true;
    if (
      (params.get('utm_source') || '').toLowerCase() === 'google' &&
      (params.get('utm_medium') || '').toLowerCase() === 'cpc'
    ) return true;
    return false;
  }

  /* ——— Derive an inc-VAT string from a free-text ex price ———
     Used for `custom.from_price` values that Liquid can't compute
     (e.g. "£0.42/cm", "From £32.65", "£1,200"). Finds the first
     numeric token, ×1.2, formats to 2 decimals, and substitutes it
     back in place — keeping the currency symbol and any suffix like
     "/cm". Commas are stripped for the calculation. */
  function fmDeriveInc(exStr) {
    if (!exStr) return exStr;
    return exStr.replace(/(\d[\d,]*(?:\.\d+)?)/, function (match) {
      var n = parseFloat(match.replace(/,/g, ''));
      if (isNaN(n)) return match;
      return (n * 1.2).toFixed(2);
    });
  }

  /* ——— Get / set mode ———
     Stored in sessionStorage (NOT localStorage) on purpose: Ex VAT must
     be the hard default for every fresh visit. sessionStorage resets
     when the browsing session ends, so Inc only ever persists *within*
     a single session that either (a) started from a Google ad, or
     (b) had the customer toggle to Inc. A new session always starts at
     the Ex default — no "stuck on Inc" across visits.
     (08-Jun-2026: switched from localStorage, which persisted Inc
     indefinitely and caused prices to show inc-VAT by default.) */
  function getMode() {
    try { return sessionStorage.getItem(STORAGE_KEY) || MODE_EX; }
    catch (e) { return MODE_EX; }
  }
  function setMode(mode) {
    try { sessionStorage.setItem(STORAGE_KEY, mode); }
    catch (e) { /* storage blocked */ }
  }

  /* ——— Apply mode to all price elements ——— */
  function applyMode(mode) {
    var isInc = (mode === MODE_INC);

    /* Mark that we're writing, so cart-observer callbacks triggered by
       these writes are ignored (released on the next macrotask). */
    fmApplying = true;
    setTimeout(function () { fmApplying = false; }, 0);

    /* Single source of truth for the whole page — set first so any
       CSS or other script can read the current mode from one place.
       Labels + prices below are all driven from this same `mode`
       value in this one function call, so they cannot diverge. */
    document.documentElement.setAttribute('data-vat-mode', mode);

    /* Price values. If an element has an explicit data-inc-price we use
       it (precise, Liquid-computed). If it has only data-ex-price (e.g.
       free-text `custom.from_price` metafields like "£0.42/cm" that
       Liquid can't VAT-compute), derive the inc value by parsing the
       number out, ×1.2, and re-inserting it — preserving any prefix/
       suffix such as the "/cm" on per-cm products. */
    var prices = document.querySelectorAll('[data-ex-price]');
    for (var i = 0; i < prices.length; i++) {
      var el = prices[i];
      var exVal  = el.getAttribute('data-ex-price');
      var incVal = el.getAttribute('data-inc-price');
      if (!incVal) incVal = fmDeriveInc(exVal);
      el.textContent = isInc ? incVal : exVal;
    }

    /* VAT labels */
    var labels = document.querySelectorAll('.fm-vat-label');
    for (var j = 0; j < labels.length; j++) {
      var lbl = labels[j];
      lbl.textContent = isInc
        ? (lbl.getAttribute('data-inc-label') || 'Inc. VAT')
        : (lbl.getAttribute('data-ex-label')  || '+ VAT');
    }

    /* Cart subtotal note */
    var notes = document.querySelectorAll('.fm-cart-vat-note');
    for (var k = 0; k < notes.length; k++) {
      var note = notes[k];
      note.textContent = isInc
        ? (note.getAttribute('data-inc-note') || 'Inc. VAT')
        : (note.getAttribute('data-ex-note')  || 'Ex. VAT — VAT added at checkout');
    }

    /* Sync toggle button state */
    var toggle = document.getElementById('fm-vat-toggle');
    if (toggle) {
      toggle.setAttribute('data-mode', mode);
      toggle.setAttribute('aria-pressed', isInc ? 'true' : 'false');
      var exBtn  = toggle.querySelector('[data-vat-btn="ex"]');
      var incBtn = toggle.querySelector('[data-vat-btn="inc"]');
      if (exBtn)  exBtn.classList.toggle('fm-vat-toggle__btn--active', !isInc);
      if (incBtn) incBtn.classList.toggle('fm-vat-toggle__btn--active',  isInc);
    }

    /* Expose mode globally so other scripts can read it */
    window.fmVatMode = mode;

    /* Notify dependent scripts (e.g. the bulk-savings subtitle, which
       derives its text from tier-price elements) so they can re-sync
       after a toggle. Fires on initial apply too. */
    document.dispatchEvent(new CustomEvent('fm:vat-changed', { detail: { mode: mode } }));
  }

  /* ——— Toggle handler ——— */
  function handleToggleClick(e) {
    var btn = e.target.closest('[data-vat-btn]');
    if (!btn) return;
    var newMode = btn.getAttribute('data-vat-btn');
    if (newMode === getMode()) return; /* already active */
    setMode(newMode);
    applyMode(newMode);
  }

  /* ——— Re-apply after cart re-renders ———
     cart-drawer.js's renderContents() replaces #CartDrawer.innerHTML on
     every add/remove/qty change, server-rendered as Ex VAT — which wipes
     our swap. We observe the cart container and re-apply the current
     mode when its contents change.

     IMPORTANT (08-Jun-2026 fix for a freeze-on-return-from-checkout bug):
       • Targets must NOT overlap. We previously observed BOTH the
         `<cart-drawer>` element AND the `#CartDrawer` div inside it.
         When applyMode wrote the cart total, each observer caught the
         other's mutation and called applyMode again — an infinite
         ping-pong that froze the page. We now observe `#CartDrawer`
         (what renderContents actually rewrites) and, separately, the
         cart-page items — these don't nest.
       • The `fmApplying` guard makes the callback ignore mutations
         caused by our own writes, so even an unforeseen overlap can't
         loop. A short debounce coalesces burst mutations. */
  function observeCartDrawer() {
    if (!window.MutationObserver) return;

    var seen = [];
    var targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('main-cart-items')
    ];

    targets.forEach(function (target) {
      if (!target) return;
      /* Guard against accidentally observing a node that contains (or is
         contained by) one we've already attached to. */
      for (var i = 0; i < seen.length; i++) {
        if (seen[i].contains(target) || target.contains(seen[i])) return;
      }
      seen.push(target);

      var debounce = null;
      var obs = new MutationObserver(function () {
        if (fmApplying) return;            /* ignore our own writes */
        if (debounce) return;              /* coalesce bursts */
        debounce = setTimeout(function () {
          debounce = null;
          applyMode(getMode());
        }, 30);
      });
      obs.observe(target, { childList: true, subtree: true });
    });
  }

  /* ——— Init ——— */
  function init() {
    /* One-time cleanup: remove any legacy localStorage value left over
       from the previous (too-sticky) implementation, so it can't keep
       forcing Inc for users who toggled or clicked a Google ad before
       the switch to sessionStorage. */
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}

    /* Default is ALWAYS Ex VAT ("+ VAT") — getMode() falls back to 'ex'
       when nothing is stored. The single exception: a Google Ads
       landing. Google Shopping requires the ad-clicked page to show
       VAT-inclusive prices, so we FORCE Inc on any landing carrying a
       Google-ad signal (gclid, or utm_source=google + utm_medium=cpc),
       overriding any previously stored preference for that entry. On
       every other visit the stored choice (or the Ex default) applies —
       so a shopper who later returns directly, or who toggles back to
       Ex, sticks with their choice. (Updated 08-Jun-2026.) */
    if (isVatInclusiveLanding()) {
      setMode(MODE_INC);
    }

    var mode = getMode();
    applyMode(mode);

    var toggle = document.getElementById('fm-vat-toggle');
    if (toggle) {
      toggle.addEventListener('click', handleToggleClick);
    }

    observeCartDrawer();
  }

  /* Run immediately on DOMContentLoaded to avoid price flash */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
